import { type ComponentProps, createElement, useEffect, useMemo, useState } from "react";
import { Armchair } from "lucide-react";
import { sileo } from "sileo";
import { type TableExplorer } from "@/components/TableExplorer";
import { tableNamesLabel } from "@/components/WaitlistCard";
import { useLocationSettings } from "@/hooks/use-locations";
import { useWaitlistSeat, useWaitlistTableSuggestions } from "@/hooks/use-waitlist";
import { apiRevertSeat, readErrorDetail } from "@/lib/reservation-api";
import { useAppStore } from "@/store/store";
import { dayjs } from "@/utils/date";
import type { ApiWaitlistEntry } from "@/types/api";
import type { WaitlistSeatPickMove } from "@/store/types";

// Fallback span used by the Explorer when location settings haven't landed
// yet. Matches the backend's DEFAULT_RESERVATION_DURATION_MINUTES.
const FALLBACK_SEAT_DURATION_MIN = 180;

/**
 * Shared seat-picker state machine for the waitlist queue card and detail
 * view. Owns the suggestions query, the operator's selection (the table
 * they chose via the Explorer drawer, overriding the auto top group), the
 * pending redistribute moves, the Sentar confirmation, and the actual
 * mutation + toast. Both surfaces consume identical behaviour.
 *
 * Selection is per-entry local state — refreshes (or switching entries)
 * reset it to the live auto top group. Persisting picks across renders
 * would require backend state and isn't worth it for what's effectively a
 * mid-flow hint.
 */
export function useWaitlistSeatPicker(
  entry: ApiWaitlistEntry,
  opts: { onSeated?: () => void } = {},
) {
  const tables = useAppStore((s) => s.tables);
  const zones = useAppStore((s) => s.zones);
  const suggestions = useWaitlistTableSuggestions(entry.id);
  const seat = useWaitlistSeat();
  const locationSettings = useLocationSettings();

  // Pick lives in the app store keyed by entryId so the queue card and the
  // detail panel see the same selection for the same entry.
  const storedPick = useAppStore((s) => s.waitlistSeatPicks[entry.id]);
  const setStoredPick = useAppStore((s) => s.setWaitlistSeatPick);
  const clearStoredPick = useAppStore((s) => s.clearWaitlistSeatPick);
  const selectedTableIds = storedPick?.tableIds ?? null;
  const pendingMoves: WaitlistSeatPickMove[] = storedPick?.moves ?? [];

  const [explorerOpen, setExplorerOpen] = useState(false);

  // Reset transient inline state on entry change. The persisted pick in
  // the store is keyed by id so it doesn't leak across entries — no need
  // to clear it here.
  useEffect(() => {
    setExplorerOpen(false);
  }, [entry.id]);

  const freeGroups = suggestions.data?.filter((s) => !s.requires_move) ?? [];
  const autoTopGroup = freeGroups[0] ?? null;
  const effectiveTableIds: string[] | null = selectedTableIds ?? autoTopGroup?.table_ids ?? null;

  // Span the Explorer asks the backend about — same window the seat
  // mutation will use (now → now + location duration). Recomputed only
  // when the entry changes so reopening the drawer doesn't shift
  // startsAt under the operator.
  const explorerWindow = useMemo(() => {
    const durationMin =
      locationSettings.data?.default_reservation_duration_minutes ?? FALLBACK_SEAT_DURATION_MIN;
    const startsAt = dayjs();
    return {
      startsAt: startsAt.toISOString(),
      endsAt: startsAt.add(durationMin, "minute").toISOString(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, locationSettings.data?.default_reservation_duration_minutes]);

  function seatNow(tableIds: string[]) {
    const moves = pendingMoves.map((m) => ({
      reservation_id: m.reservation_id,
      tables: m.tables,
    }));
    seat.mutate(
      { id: entry.id, tableIds, moves },
      {
        onSuccess: (response) => {
          // ``withOverrideRetries`` resolves to ``undefined`` when the
          // operator dismissed one of the override dialogs — the hook
          // already fired the matching toast, nothing else to do.
          if (!response) return;
          const reservationId = response.converted_reservation_id;
          // Mirror the reservation seat toast: ``sileo.action`` (blue
          // info variant) + Armchair icon + Deshacer button. Same
          // surface, same affordance — operators read one pattern.
          sileo.action({
            title: "Comensal sentado",
            description: `${entry.guest_name} → ${tableNamesLabel(tables, tableIds)}`,
            icon: createElement(Armchair, { size: 18, strokeWidth: 2.5 }),
            ...(reservationId
              ? { button: { title: "Deshacer", onClick: () => apiRevertSeat(reservationId) } }
              : {}),
          });
          // The entry left the queue; drop its stored pick so a future
          // re-seat (revert → re-add) starts from the auto top group.
          clearStoredPick(entry.id);
          opts.onSeated?.();
        },
        onError: (err) =>
          sileo.error({
            title: "No se pudo sentar",
            description: readErrorDetail(err) ?? "La mesa puede haberse ocupado. Intenta de nuevo.",
          }),
      },
    );
  }

  const explorerProps: ComponentProps<typeof TableExplorer> = {
    open: explorerOpen,
    onOpenChange: setExplorerOpen,
    context: "seat-override",
    pax: entry.pax,
    startsAt: explorerWindow.startsAt,
    endsAt: explorerWindow.endsAt,
    preferredZoneId: entry.preferred_zone_id ?? null,
    features: entry.features,
    currentTableIds: effectiveTableIds ?? [],
    onPick: (pick) => {
      setStoredPick(entry.id, { tableIds: pick.tableIds, moves: pick.moves });
      setExplorerOpen(false);
    },
    zoneName: (id) => zones.find((z) => z.id === id)?.name ?? id,
  };

  return {
    suggestions,
    autoTopGroup,
    effectiveTableIds,
    selectedTableIds,
    isSeating: seat.isPending,
    // Same window the seat mutation will use. Exposed so consumers can
    // call apiDryRun with matching bounds.
    seatWindow: explorerWindow,
    openExplorer: () => setExplorerOpen(true),
    seatNow,
    explorerProps,
  };
}
